import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRightIcon, TrashIcon, EllipsisVerticalIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import IconDrag from 'shared_items/icon_drag';
import { updateTodo } from '../../moduleServices/planner_todos_api';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import ConfirmModal from '../../moduleComponents/ConfirmModal';
import RowTodoItem from './RowTodoItem';
import RowTodoItemAdd from './RowTodoItemAdd';
import { useTodoEdit } from '../viewServices/useTodoEdit';
import { useResponsiveDrag } from 'services/useResponsiveDrag';
import { useDevice } from 'services/DeviceContext';
import { useClickOutside } from 'services/useClickOutside';
import { formatDateLocal } from 'shared_items/formatConventions';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


// ##### CardTodoListDraggable Component - Wraps CardTodoList with drag functionality
const CardTodoListDraggable = ({ todo, isDragging, onCardToggle, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: todo.id });
  
  const { dragContainerStyle } = useResponsiveDrag();
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // ##### Handle card toggle and sync with parent state
  const handleCardToggle = () => {
    if (onCardToggle) {
      onCardToggle(todo.id);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <CardTodoList
        todo={todo}
        {...props}
        dragAttributes={attributes}
        dragListeners={listeners}
        onCardToggle={handleCardToggle}
      />
    </div>
  );
};

// ##### Main CardTodoList Component
function CardTodoList({
  todo, // New prop for todo object
  title, 
  date, 
  items = [],
  onAddItem, // Unified function for adding items at any position
  onDeleteItem, 
  onDeleteTodo, 
  onToggleItem,
  onReorderItems, 
  onEditItem, 
  onUpdateTodo, // Callback to update todo in parent state
  isTodoLoading = false, 
  isDeleting = false,
  dragAttributes = {},
  dragListeners = {},
  open: externalOpen = undefined, // External open state
  onCardToggle = undefined, // External toggle handler
  calendars = [],
}) {
  // Extract props from todo object if provided
  const [localTitle, setLocalTitle] = React.useState(null);
  const [localDate, setLocalDate] = React.useState(null);
  const [showCalendarDropdown, setShowCalendarDropdown] = React.useState(false);
  const calendarDropdownRef = React.useRef(null);
  const calendarButtonRef = React.useRef(null);
  const todoTitle = localTitle !== null ? localTitle : (todo?.title || title);
  const todoDate = localDate !== null ? localDate : (todo?.date || date);
  const todoItems = todo?.items || items;
  
  // ##### Update local title and date when todo prop changes
  React.useEffect(() => {
    if (todo?.title) {
      setLocalTitle(null); // Reset local title when todo prop updates
    }
    if (todo?.date) {
      setLocalDate(null); // Reset local date when todo prop updates
    }
  }, [todo?.title, todo?.date]);
  
  // ##### Custom hooks
  const {
    isEditMode,
    newItem,
    setNewItem,
    activeId,
    showDeleteModal,
    showMenu,
    setShowMenu,
    isClosingMenu,
    isEditingTitle,
    editingTitleValue,
    isSavingTitle,
    cardRef,
    menuRef,
    buttonMenuRef, // Ref for the three dots button (for portal positioning)
    titleInputRef, // Ref for title input (for auto-focus)
    toggleEditMode,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
    handleMenuToggle,
    handleCloseMenu,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditTitleClick,
    handleTitleChange,
    handleTitleSave,
    handleTitleCancel
  } = useTodoEdit();

  // ##### Calculate portal position for dropdown menu
  const getPortalStyle = () => {
    if (!buttonMenuRef.current) return {};
    const rect = buttonMenuRef.current.getBoundingClientRect();
    const minWidth = 160; // Match min-w-[160px] from className
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.right + window.scrollX - minWidth}px`, // Align dropdown right edge to button right edge
      zIndex: 50,
      minWidth: `${minWidth}px`,
    };
  };

  // ##### Handle click outside to close menu
  useClickOutside([menuRef], handleCloseMenu, showMenu);

  // ##### Prevent card toggle when menu is open or closing
  useEffect(() => {
    if (!showMenu && !isClosingMenu) return;

    const preventActions = (e) => {
      // If menu is open or closing, prevent card toggle and other actions
      if (showMenu || isClosingMenu) {
        // Only prevent if click is outside the menu
        if (menuRef.current && !menuRef.current.contains(e.target)) {
          // Check if click is on the card button
          if (cardRef.current && cardRef.current.contains(e.target)) {
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }
    };

    // Use capture phase to intercept BEFORE other handlers
    document.addEventListener('mousedown', preventActions, true);
    document.addEventListener('click', preventActions, true);
    document.addEventListener('touchstart', preventActions, true);

    return () => {
      document.removeEventListener('mousedown', preventActions, true);
      document.removeEventListener('click', preventActions, true);
      document.removeEventListener('touchstart', preventActions, true);
    };
  }, [showMenu, isClosingMenu]);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : true;

  // ##### Device Detection
  const { isMobile } = useDevice();

  // ##### Responsive Drag Configuration
  const { 
    activationDistance, 
    activationDelay, 
    activationTolerance, 
    useTouchSensors,
    dragHandleStyle,
    dragHandleClass
  } = useResponsiveDrag();

  // ##### Drag and Drop Configuration
  const pointerSensor = useSensor(PointerSensor, { 
    activationConstraint: { distance: activationDistance } 
  });
  
  const touchSensor = useSensor(TouchSensor, { 
    activationConstraint: { 
      delay: activationDelay, 
      tolerance: activationTolerance 
    } 
  });
  
  const sensors = useSensors(
    pointerSensor,
    ...(useTouchSensors ? [touchSensor] : [])
  );


  // ##### Event Handlers
  const handleAddItemWrapper = (itemText) => {
    onAddItem(itemText);
    setNewItem('');
  };

  const handleDragEndWrapper = (event) => {
    handleDragEnd(event, todoItems, onReorderItems);
  };

  const handleCardToggle = (e) => {
    // Prevent toggle if menu is closing or title is being edited
    if (isClosingMenu || showMenu || isEditingTitle) {
      e?.stopPropagation();
      return;
    }
    
    if (onCardToggle) {
      onCardToggle();
    }
    // Always reset to readonly mode when toggling
    if (isEditMode) {
      toggleEditMode();
    }
  };

  const handleConfirmDeleteWrapper = () => {
    handleConfirmDelete(onDeleteTodo);
  };

  // ##### Handle title save wrapper
  const handleTitleSaveWrapper = async (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (todo?.id && editingTitleValue.trim()) {
      const savedTitle = editingTitleValue.trim();
      const currentDate = formatDateLocal(new Date());
      // Optimistic update - update local state immediately
      setLocalTitle(savedTitle);
      setLocalDate(currentDate);
      try {
        const updatedTodo = await handleTitleSave(updateTodo, todo.id);
        // Update parent state with updated todo
        if (updatedTodo && onUpdateTodo) {
          onUpdateTodo(updatedTodo);
        }
        // State will be reset by handleTitleSave in the hook
      } catch (error) {
        console.error('Error saving title:', error);
        // Revert optimistic update on error
        setLocalTitle(null);
        setLocalDate(null);
      }
    }
  };

  // ##### Handle title cancel wrapper
  const handleTitleCancelWrapper = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setLocalTitle(null); // Reset local title to use prop value
    handleTitleCancel();
  };

  // ##### Handle title key press (Enter to save)
  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTitleSaveWrapper();
    }
  };

  // ##### Handle calendar select (optimistic update, then sync with server)
  const handleCalendarSelect = async (calendarId) => {
    if (!todo?.id || !onUpdateTodo) return;
    setShowCalendarDropdown(false);
    const previousTodo = { ...todo };
    const selectedCalendar = calendarId != null ? calendars.find(c => c.id === calendarId) : null;
    const optimisticTodo = { ...todo, calendar: selectedCalendar || null, calendar_id: calendarId };
    onUpdateTodo(optimisticTodo);
    try {
      const updatedTodo = await updateTodo(todo.id, { calendar_id: calendarId });
      onUpdateTodo(updatedTodo);
    } catch (err) {
      console.error('Error updating todo calendar:', err);
      onUpdateTodo(previousTodo);
    }
  };

  // ##### Click outside to close calendar dropdown
  useClickOutside([calendarDropdownRef, calendarButtonRef], () => setShowCalendarDropdown(false), showCalendarDropdown);

  // ##### Portal position for calendar dropdown (below trigger button, right-aligned)
  const getCalendarDropdownPortalStyle = () => {
    if (!calendarButtonRef.current) return {};
    const rect = calendarButtonRef.current.getBoundingClientRect();
    const minWidth = 200;
    return {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.right - minWidth}px`,
      zIndex: 50,
      minWidth: `${minWidth}px`,
    };
  };


  // ##### Loading State - Task Deletion
  if (isDeleting) {
    return (
      <div className="bg-white rounded-2xl shadow p-0 overflow-hidden flex flex-col h-28 relative">
        <div className="px-4 py-3 space-y-2">
          <div className="h-4 bg-gray-400 rounded animate-pulse"></div>
          <div className="h-4 bg-gray-400 rounded animate-pulse w-1/2"></div>
        </div>
        <div className="px-4 py-3 space-y-2">
          <div className="h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-300 rounded animate-pulse w-3/4"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60">
          <SpinnerLoad />
        </div>
      </div>
    );
  }

  // ##### Main Render
  return (
    <>
      {/* ##### Overlay to prevent clicks when menu is open */}
      {showMenu && (
        <div
          className="fixed inset-0 z-[45]"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCloseMenu();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCloseMenu();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCloseMenu();
          }}
        />
      )}
      
      <div
        ref={cardRef}
        className={`bg-white rounded-2xl shadow p-0 overflow-hidden flex flex-col hover:shadow-xl transition-shadow duration-300 ease-out ${(todo?.calendar?.color ?? (todo?.calendar_id && calendars.find(c => c.id === todo.calendar_id)?.color)) ? 'border-l-4' : ''}`}
        style={(() => {
          const calendarColor = todo?.calendar?.color ?? (todo?.calendar_id && calendars.find(c => c.id === todo.calendar_id)?.color);
          return calendarColor ? { borderLeftColor: calendarColor } : {};
        })()}
      >
      {/* ##### Card Header */}
      <div className="w-full flex items-center px-4 py-3 focus:outline-none hover:bg-blue-50 transition">
        {/* ##### Drag Handle - Left side */}
        <div className="flex-shrink-0 mr-2">
          <button
            {...dragAttributes}
            {...dragListeners}
            className="cursor-grab active:cursor-grabbing flex items-center justify-center select-none p-0"
            style={dragHandleStyle}
            tabIndex={-1}
            type="button"
            onClick={(e) => e.stopPropagation()} // Prevent card toggle when clicking drag handle
            onTouchStart={(e) => e.stopPropagation()}
          >
            <IconDrag className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* ##### Card Content - Clickable area for toggle (only when NOT editing title) */}
        {!isEditingTitle ? (
          <button
            className="flex-1 flex flex-row-reverse justify-between items-center focus:outline-none"
            onClick={(e) => {
              // Prevent if menu is open or closing
              if (showMenu || isClosingMenu) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
              handleCardToggle(e);
            }}
            onMouseDown={(e) => {
              // Prevent if menu is open or closing
              if (showMenu || isClosingMenu) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
            }}
            onTouchStart={(e) => {
              // Prevent if menu is open or closing
              if (showMenu || isClosingMenu) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
            }}
          >
            <div className="flex items-center gap-2 ml-2">
              {/* Done Editing Button - Only in edit mode */}
              {isEditMode && (
                <button
                  type="button"
                  className="px-3 py-1 text-xs text-white bg-blue-700 hover:bg-blue-700 rounded-lg shadow-md hover:scale-105 transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEditMode();
                  }}
                >
                  Done
                </button>
              )}
              
              {/* Three Dots Menu */}
              {onDeleteTodo && (
                <div className="relative">
                  {isTodoLoading ? (
                    <div className="p-2 flex items-center justify-center w-10 h-10">
                      <SpinnerLoad size="small" />
                    </div>
                  ) : (
                    <>
                      <button
                        ref={buttonMenuRef}
                        type="button"
                        className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                        onClick={handleMenuToggle}
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                      
                      {/* Dropdown Menu - Using Portal to render outside card container */}
                      {showMenu && createPortal(
                        <div style={getPortalStyle()}>
                          <div
                            ref={menuRef}
                            className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]"
                          >
                            <button
                              onClick={(e) => handleEditTitleClick(e, todoTitle)}
                              disabled={isTodoLoading || isEditingTitle}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              onClick={handleDeleteClick}
                              disabled={isTodoLoading}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </div>,
                        document.body
                      )}
                    </>
                  )}
                </div>
              )}
              
              {/* Expand/Collapse Icon */}
              <ChevronRightIcon
                className={`w-5 h-5 text-gray-700 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                aria-hidden="true"
              />
            </div>
            
            {/* Todo Title and Date */}
            <div className="flex flex-col text-left flex-1">
              <span className="font-semibold text-base text-gray-800">{todoTitle}</span>
              {!isEditingTitle && (
                <span className="text-xs text-gray-400">{todoDate}</span>
              )}
            </div>
          </button>
        ) : (
          /* ##### Title Edit Mode - Separate container when editing */
          <div className="flex-1 flex flex-row-reverse justify-between items-center">
            <div className="flex items-center gap-2 ml-2">
              {/* Three Dots Menu - Hidden when editing title */}
              {onDeleteTodo && !isEditingTitle && (
                <div className="relative">
                  {isTodoLoading ? (
                    <div className="p-2 flex items-center justify-center w-10 h-10">
                      <SpinnerLoad size="small" />
                    </div>
                  ) : (
                    <>
                      <button
                        ref={buttonMenuRef}
                        type="button"
                        className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                        onClick={handleMenuToggle}
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              )}
              
              {/* Expand/Collapse Icon */}
              <ChevronRightIcon
                className={`w-5 h-5 text-gray-700 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                aria-hidden="true"
              />
            </div>
            
            {/* Task Title Edit Input and Buttons */}
            <div className="flex flex-col text-left flex-1">
              <div className="flex items-center gap-2">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editingTitleValue}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onKeyDown={handleTitleKeyPress}
                  className="flex-1 font-semibold text-base text-gray-800 bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-700"
                  disabled={isSavingTitle}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                {/* Calendar circle selector - dropdown rendered in portal */}
                <div className="relative flex-shrink-0">
                  <button
                    ref={calendarButtonRef}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowCalendarDropdown(!showCalendarDropdown);
                    }}
                    className="w-8 h-8 rounded-full border-2 hover:opacity-80 transition-opacity flex items-center justify-center shadow-sm"
                    style={(() => {
                      const selectedCalendarId = todo?.calendar?.id ?? todo?.calendar_id ?? null;
                      const selectedCalendar = selectedCalendarId
                        ? calendars.find(c => c.id === selectedCalendarId)
                        : null;
                      const calendarColor = selectedCalendar?.color || '#9CA3AF';
                      const borderColor = selectedCalendar ? calendarColor : '#D1D5DB';
                      return { backgroundColor: calendarColor, borderColor };
                    })()}
                    title={(() => {
                      const selectedCalendarId = todo?.calendar?.id ?? todo?.calendar_id ?? null;
                      const selectedCalendar = selectedCalendarId
                        ? calendars.find(c => c.id === selectedCalendarId)
                        : null;
                      return selectedCalendar?.name || 'No calendar - Click to assign';
                    })()}
                  />
                  {showCalendarDropdown && createPortal(
                    <div style={getCalendarDropdownPortalStyle()}>
                      <div
                        ref={calendarDropdownRef}
                        className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] max-h-[360px] overflow-y-auto"
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleCalendarSelect(null);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                            !(todo?.calendar?.id ?? todo?.calendar_id) ? 'text-green-600 bg-green-50' : 'text-gray-700'
                          }`}
                        >
                          <div className="w-3 h-3 rounded-full border border-gray-300" />
                          <span>No calendar</span>
                        </button>
                        {calendars.map((cal) => (
                          <button
                            key={cal.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleCalendarSelect(cal.id);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                              (todo?.calendar?.id ?? todo?.calendar_id) === cal.id ? 'text-green-600 bg-green-50' : 'text-gray-700'
                            }`}
                          >
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: cal.color }}
                            />
                            <span>{cal.name}</span>
                          </button>
                        ))}
                        {calendars.length === 0 && (
                          <div className="px-4 py-2 text-xs text-gray-500 italic">
                            No calendars available
                          </div>
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleTitleSaveWrapper();
                  }}
                  disabled={isSavingTitle || !editingTitleValue.trim()}
                  className="p-1 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingTitle ? (
                    <SpinnerLoad size="small" />
                  ) : (
                    <CheckIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleTitleCancelWrapper();
                  }}
                  disabled={isSavingTitle}
                  className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ##### Card Content - Only when expanded */}
      {open && (
        <div className="px-4 pb-4 pt-2">
          {/* ##### Items List - Conditional rendering based on mode */}
          {isEditMode ? (
            // ##### Edit Mode - With drag and drop functionality and draggable add item
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEndWrapper}
            >
              <SortableContext
                items={todoItems.map(i => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {todoItems
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map(item => (
                      <RowTodoItem
                        key={item.id}
                        item={item}
                        onToggleItem={onToggleItem}
                        onDeleteItem={onDeleteItem}
                        onEditItem={onEditItem}
                        isEditMode={isEditMode}
                      />
                    ))}
                  
                  {/* Add Item - Always at the end */}
                  <RowTodoItemAdd
                    newItem={newItem}
                    setNewItem={setNewItem}
                    onAddItem={handleAddItemWrapper}
                    isEditMode={isEditMode}
                  />
                </ul>
              </SortableContext>
              
              {/* ##### Drag Overlay */}
              <DragOverlay>
                {activeId ? (
                  <div className="opacity-50 scale-105 shadow-2xl">
                    {activeId === 'add-item' ? null : (
                      (() => {
                        const activeItem = todoItems.find(item => item.id === activeId);
                        console.log('👻 Overlay rendering:', activeId, activeItem);
                        return activeItem ? (
                          <RowTodoItem
                            item={activeItem}
                            isEditMode={isEditMode}
                            onToggleItem={onToggleItem}
                            onDeleteItem={onDeleteItem}
                            onEditItem={() => {}}
                            dragProps={{
                              listeners: {},
                              attributes: {},
                              transform: null,
                              transition: null,
                              isDragging: false
                            }}
                          />
                        ) : null;
                      })()
                    )}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            // ##### Readonly Mode - Clickable list without drag functionality
            <ul className="space-y-2 cursor-pointer rounded-lg p-2 -m-2" onClick={toggleEditMode}>
              {todoItems
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(item => (
                  <RowTodoItem
                    key={item.id}
                    item={item}
                    isEditMode={false}
                    onToggleItem={onToggleItem}
                  />
                ))}
            </ul>
          )}

        </div>
      )}

      {/* ##### Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDeleteWrapper}
        title="Delete Todo"
        message={`Are you sure you want to delete "${todoTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
      />
      </div>
    </>
  );
}

// ##### Export both components
export { CardTodoListDraggable };
export default CardTodoList;